import { useEffect, useState, useMemo } from "react";
import DateInput from '../../components/DateInput';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../config";
import { showSuccess } from "../../utils/dialog";
import { SignaturePanel } from "../../shared/SignaturePanel";
import "../../Styles/AccessibilityStndCustmer.css";
import talentLogo from "../../assets/talentlogo2.png";

const AccessibilityForm = () => {
  const navigate = useNavigate();
  const itemId = "accessibility";
  const fieldOrder = useMemo(() => ["date", "employeeName", "employeeSignature"], []);
  const [formData, setFormData] = useState(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    const initial = saved ? JSON.parse(saved) : {};
    return fieldOrder.reduce((acc, f) => {
      acc[f] = (initial[f] ?? "");
      return acc;
    }, {});
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [err, setErr] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // load from storage on mount (normalize)
  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    if (saved) {
      const raw = JSON.parse(saved);
      const normalized = fieldOrder.reduce((acc, f) => {
        acc[f] = (raw[f] ?? "");
        return acc;
      }, {});
      setFormData(normalized);
    }
  }, [fieldOrder, itemId]);

  // const isDateToday = (val) => {
  //   if (!val) return false;
  //   const input = new Date(val);
  //   const now = new Date();
  //   return (
  //     input.getFullYear() === now.getFullYear() &&
  //     input.getMonth() === now.getMonth() &&
  //     input.getDate() === now.getDate()
  //   );
  // };

  const isDateToday = (val) => {
    if (!val) return false;
    
    // Get current date in User's Local Time
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(now.getDate()).padStart(2, '0');

    // Create a standardized string "YYYY-MM-DD"
    const todayString = `${year}-${month}-${day}`;

    // Compare the strings directly
    return val === todayString;
  };
  // Centralized validation used by both live status updates and the submit handler
  const validateForm = (data) => {
    const errors = {};
    if (!data.date) errors.date = "This field is required";
    else if (!isDateToday(data.date)) errors.date = "Date must be today";
    if (!String(data.employeeName || "").trim()) errors.employeeName = "This field is required";
    if (!data.employeeSignature) errors.employeeSignature = "Signature is required";

    const values = fieldOrder.map((f) => (data[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const allFilled = values.every((v) => v.length !== 0);
    const dateValid = !errors.date;
    const nameValid = !errors.employeeName;
    const signatureValid = !errors.employeeSignature;

    const status = allEmpty
      ? "incomplete"
      : allFilled && dateValid && nameValid && signatureValid
      ? "complete"
      : "in-progress";

    return { errors, status };
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setErr("");
    setFieldErrors({});
    setIsSubmitting(true);

    const accountId = localStorage.getItem("accountId");
    if (!accountId) {
      setIsSubmitting(false);
      return navigate("/register");
    }

    // Persist locally for fast reload
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));

    const { errors, status: newStatus } = validateForm(formData);
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setErr("Please fill the required fields!");
      setIsSubmitting(false);
      return;
    }

    const progress = newStatus === "complete" ? 100 : newStatus === "in-progress" ? 50 : 0;

    try {
      if (newStatus === "complete") {
        // Mark item complete and then update overall registration status for this item
        await axios.post(`${BASE_URL}/api/registration/${accountId}/items/${itemId}/complete`, {
          status: newStatus,
          progress,
          formData,
        });
        await axios
          .post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
            status: newStatus,
            formData: {},
          })
          .catch(() => {});
      } else {
        // Not fully complete yet, just sync status
        await axios
          .post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
            status: newStatus,
            formData: {},
          })
          .catch(() => {});
      }
      localStorage.setItem(`status_${accountId}_${itemId}`, newStatus);
    } catch (saveErr) {
      console.error("Failed to save Accessibility form to server:", saveErr);
      // Continue with navigation even if server save fails
    } finally {
      setIsSubmitting(false);
    }

    if (newStatus === "complete") {
      showSuccess("Form completed successfully!");
    }
    navigate("/register");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      if (String(value || "").trim()) delete next[field];
      return next;
    });
  };

  // Auto-save draft. Never set "complete" here (only on Submit). If already complete, preserve it when user reopens and goes back.
  useEffect(() => {
    // Don't auto-save if currently submitting to avoid conflicts
    if (isSubmitting) return;
    
    const accountId = localStorage.getItem("accountId");
    const existing = (accountId ? localStorage.getItem(`status_${accountId}_${itemId}`) : null) || localStorage.getItem(`status_${itemId}`);
    const { status } = validateForm(formData);
    // Never auto-upgrade to "complete" – only keep it if it was already complete
    const statusToPersist = status === "complete" ? (existing === "complete" ? "complete" : "in-progress") : status;
    
    localStorage.setItem(`status_${accountId}_${itemId}`, statusToPersist);
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    
    // Only sync to server if status changed and not during submission
    if (accountId && statusToPersist !== "incomplete" && existing !== statusToPersist) {
      axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
        status: statusToPersist,
        formData: {}
      }).catch(err => console.error("Failed to sync status:", err));
    }
  }, [formData, isSubmitting, fieldOrder, itemId]);

  const handleBack = () => {
    navigate("/register");
  };

  return (
    <div className="accessibility-form-page">
      <div className="form-container">
        {/* Top content card */}
        <div className="form-card">
          <header className="form-card-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 64, marginBottom: 10 }} />
            <h2 className="form-title" style={{ textAlign: "center" }}>
              Accessibility Standards for Customer Service
            </h2>
          </header>

          <section className="form-card-content">
            {err && (<div className="error-text" style={{ color: "#dc2626", marginBottom: 12 }}>{err}</div>)}
            <p className="form-intro">
              The Accessibility for Ontarians with Disabilities Act, 2005 (AODA) is a law in Ontario that allows the government to develop specific standards of accessibility and to enforce them. Recognizing the history of discrimination against persons with disabilities in Ontario, the purpose of this Act is to benefit all Ontarians by:
            </p>
            <ul className="form-bullets topbullets">
              <li>Developing, implementing, and enforcing accessibility standards to achieve accessibility for Ontarians with disabilities with respect to goods, services, facilities, accommodation, employment, buildings, structures, and premises on or before January 1, 2025; and</li>
              <li>Providing for the involvement of persons with disabilities, the Government of Ontario and of representatives of industries and various sectors of the economy in the development of accessibility standards.</li>
            </ul>
            <p className="form-intro paragraph-space">
              The standards require the people or organizations identified in the standard to identify, remove, and prevent barriers for people with disabilities in key areas of daily living. Barriers keep people with disabilities from fully participating in activities that most of us take for granted. The customer service standard is the first standard to come into effect under the AODA.
            </p>
            <p className="form-intro paragraph-space">
              Accessible customer service is about understanding that people with disabilities may have different needs and finding the best way to help them access goods and services. It can be as easy as asking “How can I help?” and making small changes to how you serve customers with disabilities. To ensure that all employees are providing accessible customer service, all employees are required to familiarize themselves with the best practices outlined below. By signing this document, the reader states that (s)he has read, understands, and agrees to follow all best practices for serving people with disabilities.
            </p>
            <p className="form-intro">
              Clients may have additional accessibility policies and training requirements according to their unique services and available assistive devices. Clients should provide required training upon shift orientation. 
            </p>
            <p className="form-intro warning">
              *Accessibility Plan and Policies are available upon request through Reception*
            </p>
            <p className="form-intro topic1">
              GENERAL TIPS FOR SERVING CUSTOMERS WITH DISABILITIES
            </p>
            <ul className="form-bullets">
              <li>• Ask "May I help you?" Your customers with disabilities know if they need help and how you can provide it.</li>
              <li>• Speak directly to your customer.</li>
              <li>• Not everybody with the same disability experiences the same things. Don’t make assumptions. Customers are not required to tell you about their disabilities.</li>
              <li>• Take the time to get to know your customer’s needs and focus on meeting those needs. Some disabilities are not visible.</li>
              <li>• All customers have a range of needs and preferences and so do your customers with disabilities.</li>
              <li>• If you can’t understand what your customer is saying, politely ask him or her to repeat it.</li>
              <li>• You may want to ask if the information you are conveying needs to be repeated. Ask: “Do you understand this?”</li>
              <li>• Exercise patience.</li>
            </ul>
            <p className="form-intro topic1">
              SERVING DEAFBLIND CUSTOMERS
            </p>
            <ul className="form-bullets">
              <li>• Speak directly to your customer, not to the intervenor.</li>
              <li>• Identify yourself to the intervenor.</li>
              <li>• A customer who is deafblind is likely to explain to you how to communicate with them.</li>
            </ul>
            <p className="form-intro topic1">
              SERVING CUSTOMERS WITH INTELLECTUAL OR DEVELOPMENTAL DISABILITIES
            </p>
            <ul className="form-bullets">
              <li>• Don’t assume what a person can or cannot do.</li>
              <li>• Use plain language.</li>
              <li>• Make sure your customer understands what you’ve said. Be direct and ask: “Do you understand this?”</li>
              <li>• Provide one piece of information at a time. You can break down the information into simpler concepts, without exaggerating speech or gestures or being patronizing.</li>
              <li>• You may want to ask if the information needs to be repeated.</li>
            </ul>
            <p className="form-intro topic1">
              SERVING DEAF, ORAL DEAF, DEAFENED, AND HARD OF HEARING CUSTOMERS
            </p>
            <ul className="form-bullets">
              <li>• Don’t shout.</li>
              <li>• Attract the customer’s attention before speaking. For example, try a gentle touch on the shoulder or wave of your hand.</li>
              <li>• Make sure you are in a well-lit area where your customers can see your face.</li>
              <li>• If the person uses a hearing aid, reduce background noise, or move to a quieter area.</li>
            </ul>
            <p className="form-intro topic1">
              SERVING CUSTOMERS WITH LEARNING DISABILITIES
            </p>
            <ul className="form-bullets">
              <li>• Take some time — people with some kinds of learning disabilities may take a little longer to process, understand and respond.</li>
              <li>• Provide information in a way that works for your customer. For example, keep a pen and paper handy. That way, you can explain, and then review and repeat the information using any additional notes.</li>
              <li>• Be prepared to explain any materials you provide for your customers.</li>
            </ul>
            <p className="form-intro topic1">
              SERVING CUSTOMERS WITH MENTAL HEALTH DISABILITIES
            </p>
            <ul className="form-bullets">
              <li>• Be confident and reassuring. As with all customers, listen carefully and focus on meeting the customer’s needs.</li>
              <li>• If the person appears to be in a crisis, ask them to tell you the best way to help.</li>
              <li>• If a customer appears to show signs of a mental health disability, it may be helpful to keep in mind that the customer’s reactions are not connected to you personally as a service provider or service agent. The customer is simply showing symptoms of mental illness.</li>
            </ul>
            <p className="form-intro topic1">
              SERVING CUSTOMERS WITH PHYSICAL DISABILITIES OR DISABILITIES AFFECTING MOBILITY
            </p>
            <ul className="form-bullets">
              <li>• Ask before you help. People with physical disabilities often have their own ways of doing things.</li>
              <li>• Respect your customer’s personal space. Don’t lean over them or on an assistive device.</li>
              <li>• Don’t move items, such as canes and walkers, out of the person’s reach.</li>
              <li>• If you have permission to move a person in a wheelchair remember to make sure your customer is ready to be moved and that you describe what you’re going to do beforehand. Don’t leave the individual in an awkward, dangerous or undignified position such as facing a wall or in the path of opening doors.</li>
              <li>• In some situations, inform your customer about accessible features in the immediate environment (automatic doors, accessible washrooms, elevators, ramps, etc.).</li>
            </ul>
            <p className="form-intro topic1">
              SERVING CUSTOMERS WITH SPEECH OR LANGUAGE IMPAIRMENTS
            </p>
            <ul className="form-bullets">
              <li>• Don’t assume that just because a person has this disability, they also have another.</li>
              <li>• Give your customer whatever time they need to get their point across.</li>
              <li>• Ask questions that can be answered “yes” or “no,” if possible.</li>
              <li>• Don’t interrupt or finish your customer’s sentences. Wait for them to finish.</li>
            </ul>
            <p className="form-intro topic1">
              SERVING CUSTOMERS WITH VISION DISABILITIES
            </p>
            <ul className="form-bullets">
              <li>• Nine out of ten people who come to CNIB have some degree of vision</li>
              <li>• Allow assistive devices, allow service animals</li>
              <li>• Have large print versions available, have magnifier available</li>
            </ul>
            <p className="form-intro topic1">
              SERVING CUSTOMERS WITH VISION LOSS
            </p>
            <ul className="form-bullets">
              <li>• Don't assume the individual can't see you.</li>
              <li>• Identify yourself when you approach your customer and speak directly to him or her.</li>
              <li>• Offer your elbow to guide the person. If they accept, walk slowly, but wait for permission before doing so.</li>
              <li>• Identify landmarks or other details to orient your customer to the environment around them.</li>
              <li>• If you’re giving directions or providing any information, be precise and descriptive. For example, if you’re approaching a door or an obstacle, say so.</li>
              <li>• Don't leave your customer in the middle of a room. Guide them to a chair or a comfortable location. Don't walk away without saying good-bye.</li>
            </ul>
            <p className="form-intro topic1">
              CUSTOMERS WITH SERVICE ANIMALS
            </p>
            <ul className="form-bullets">
              <li>• Service animals are used for the following:</li>
              <li>• Vision loss</li>
              <li>• Hearing alert animals</li>
              <li>• To alert an individual to an oncoming seizure</li>
              <li>• Helping people with: autism, mental health disabilities, physical disabilities, Other disabilities</li>
              <li>• Service Animals are allowed anywhere customers normally have access.</li>
            </ul>
            <p className="form-intro topic1">
              CUSTOMERS WITH SUPPORT PERSONS
            </p>
            <ul className="form-bullets">
              <li>• Assistive devices are part of people’s personal space</li>
              <li>• Respect personal space</li>
              <li>• Know how to operate any assistive devices your organization provides</li>
            </ul>
            <p className="form-intro topic1">
              PERSONAL ASSISTIVE DEVICES
            </p>
            <ul className="form-bullets">
              <li>• Assistive devices are part of people’s personal space</li>
              <li>• Respect personal space</li>
              <li>• Know how to operate any assistive devices your organization provides</li>
            </ul>
            <p className="form-intro topic1">
              IF THERE ARE DIFFICULTIES ACCESSING YOUR SERVICES
            </p>
            <ul className="form-bullets">
              <li>• Be flexible</li>
              <li>• Ask: “May I Help You?”</li>
              <li>• Solicit suggestions</li>
              <li>• Offer to communicate via pen and paper</li>
              <li>• Retrieve out-of-reach products</li>
              <li>• Confirm that needs are met</li>
            </ul>
            <p className="form-intro paragraph-space bottomsentence">
              By signing below, I acknowledge that I have read & understand the information above regarding accessible customer service standards.
            </p>
          </section>
          <div className="grid-2">
            <div className="form-field">
              <label htmlFor="date">Date*</label>
              <DateInput
                id="date"
                value={formData.date}
                onChange={(v) => handleInputChange("date", v)}
                required
              />
              {fieldErrors.date && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.date}</div>}
            </div>

            <div className="form-field">
              <label htmlFor="employeeName">Employee's Full Name*</label>
              <input
                id="employeeName"
                type="text"
                value={formData.employeeName}
                onChange={(e) =>
                  handleInputChange("employeeName", e.target.value)
                }
                required
              />
              {fieldErrors.employeeName && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeName}</div>}
            </div>
          </div>

          {/* Signature panel in a boxed wrapper */}
          <div className="signature-panel-wrapper">
            {/* <label>Employee's Signature*</label> */}
            <SignaturePanel
              onSignatureChange={(dataURL) =>
                handleInputChange("employeeSignature", dataURL)
              }
              initialSignature={formData.employeeSignature}
              required
            />
            {fieldErrors.employeeSignature && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeSignature}</div>}
          </div>

          <div className="form-buttons">
            <button type="button" onClick={handleBack} disabled={isSubmitting}>Back</button>
            <button type="submit" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityForm;
